<template>
  <div
    class="flex items-center space-x-2 sm:space-x-3 pl-2 sm:pl-4 border-l border-slate-200/70 dark:border-slate-800"
  >
    <!-- Enhanced User Info -->
    <div class="text-right hidden xl:block">
      <div class="text-xs sm:text-sm font-bold text-slate-900 dark:text-white leading-tight">
        {{ userName }}
      </div>
      <div class="text-[11px] text-slate-500 dark:text-slate-400 capitalize mt-0.5 font-medium">
        {{ userRole }}
      </div>
    </div>

    <!-- Enhanced Profile Dropdown -->
    <div class="relative your-profile-wrapper">
      <button
        @click="toggleUserMenu"
        class="flex items-center gap-1.5 sm:gap-2 p-1 sm:p-1.5 rounded-xl hover:bg-slate-100/70 dark:hover:bg-slate-800/60 transition-all duration-150 border border-transparent hover:border-slate-200/80 dark:hover:border-slate-700/60 group cursor-pointer"
        aria-label="User menu"
      >
        <div
          class="w-8 h-8 sm:w-9 sm:h-9 rounded-xl overflow-hidden border border-slate-200/80 dark:border-slate-700 flex items-center justify-center bg-gradient-to-tr from-indigo-100 to-violet-100 dark:from-indigo-950 dark:to-violet-950 shadow-2xs group-hover:scale-105 transition-transform"
        >
          <img
            v-if="userPhoto"
            :src="userPhoto"
            alt="User Photo"
            class="w-full h-full object-cover"
          />
          <User
            v-else
            class="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600 dark:text-indigo-400"
          />
        </div>

        <ChevronDown
          :class="[
            'w-3.5 h-3.5 text-slate-400 dark:text-slate-500 hidden sm:block transition-transform duration-200 group-hover:text-slate-600 dark:group-hover:text-slate-300',
            showUserMenu ? 'rotate-180' : '',
          ]"
        />
      </button>

      <!-- Dropdown Menu -->
      <div
        v-if="showUserMenu"
        class="absolute right-0 mt-3 w-60 bg-white dark:bg-[#111C30] rounded-2xl shadow-xl py-2 z-50 border border-slate-200 dark:border-[#263449] animate-fade-in backdrop-blur-sm"
      >
        <!-- Menu Items -->
        <div class="py-2">
          <!-- Profile -->
          <router-link
            to="/profile"
            class="flex items-center px-4 py-3 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white transition-all duration-200 group"
          >
            <div
              class="p-2 bg-blue-100 dark:bg-blue-950/60 rounded-lg mr-3 group-hover:bg-blue-200 dark:group-hover:bg-blue-900/60 transition-colors duration-200"
            >
              <User class="w-4 h-4 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <div class="font-medium">Profile</div>
              <div class="text-xs text-slate-500 dark:text-slate-400">View and edit profile</div>
            </div>
          </router-link>

          <!-- Toggle Theme in Profile Menu -->
          <button
            @click="toggleTheme"
            class="w-full flex items-center px-4 py-3 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white transition-all duration-200 group text-left"
          >
            <div
              class="p-2 rounded-lg mr-3 transition-colors duration-200"
              :class="isDark ? 'bg-amber-950/60 text-amber-400' : 'bg-indigo-100 text-indigo-600'"
            >
              <Sun v-if="isDark" class="w-4 h-4" />
              <Moon v-else class="w-4 h-4" />
            </div>
            <div class="flex-1">
              <div class="font-medium flex items-center justify-between">
                <span>Theme</span>
                <span class="text-xs px-2 py-0.5 rounded-full font-medium" :class="isDark ? 'bg-slate-800 text-amber-300' : 'bg-slate-100 text-indigo-700'">
                  {{ isDark ? 'Dark' : 'Light' }}
                </span>
              </div>
              <div class="text-xs text-slate-500 dark:text-slate-400">
                Switch to {{ isDark ? 'light' : 'dark' }} mode
              </div>
            </div>
          </button>

          <!-- Conditional School Menu Items -->
          <template v-if="hasSchool">
            <!-- School Details -->
            <router-link
              to="/schoolDetails"
              class="flex items-center px-4 py-3 text-sm text-slate-700 dark:text-slate-200 hover:bg-cyan-50 dark:hover:bg-cyan-950/40 hover:text-cyan-900 dark:hover:text-cyan-300 transition-all duration-200 group"
            >
              <div
                class="p-2 bg-cyan-100 dark:bg-cyan-950/60 rounded-lg mr-3 group-hover:bg-cyan-200 dark:group-hover:bg-cyan-900/60 transition-colors duration-200"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="w-4 h-4 text-cyan-600 dark:text-cyan-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M3 7h18M3 12h18M3 17h18"
                  />
                </svg>
              </div>
              <div>
                <div class="font-medium">School Details</div>
                <div class="text-xs text-slate-500 dark:text-slate-400">
                  View school information
                </div>
              </div>
            </router-link>
          </template>

          <template v-else-if="userRole === 'admin' && !hasSchool">
            <!-- Create School -->
            <router-link
              to="/createSchool"
              class="flex items-center px-4 py-3 text-sm text-slate-700 dark:text-slate-200 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 hover:text-indigo-900 dark:hover:text-indigo-300 transition-all duration-200 group"
            >
              <div
                class="p-2 bg-indigo-100 dark:bg-indigo-950/60 rounded-lg mr-3 group-hover:bg-indigo-200 dark:group-hover:bg-indigo-900/60 transition-colors duration-200"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="w-4 h-4 text-indigo-600 dark:text-indigo-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 4v16m8-8H4"
                  />
                </svg>
              </div>
              <div>
                <div class="font-medium">Create School</div>
                <div class="text-xs text-slate-500 dark:text-slate-400">Add a new school</div>
              </div>
            </router-link>
          </template>

          <!-- Divider -->
          <div class="border-t border-slate-100 dark:border-[#263449] my-2"></div>

          <!-- Sign Out -->
          <button
            @click="handleLogout"
            class="w-full flex items-center px-4 py-3 text-sm text-slate-700 dark:text-slate-200 hover:bg-red-50 dark:hover:bg-red-950/40 hover:text-red-700 dark:hover:text-red-400 transition-all duration-200 group text-left"
          >
            <div
              class="p-2 bg-red-100 dark:bg-red-950/60 rounded-lg mr-3 group-hover:bg-red-200 dark:group-hover:bg-red-900/60 transition-colors duration-200"
            >
              <LogOut class="w-4 h-4 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <div class="font-medium">Sign out</div>
              <div class="text-xs text-slate-500 dark:text-slate-400">End your session</div>
            </div>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, onMounted } from "vue";
  import { useRouter } from "vue-router";
  import { User, ChevronDown, LogOut, Sun, Moon } from "lucide-vue-next";
  import store from "@/store";
  import { useTheme } from "@/composables/useTheme";

  const router = useRouter();
  const { isDark, toggleTheme } = useTheme();

  // State
  const showUserMenu = ref(false);
  const showSearch = ref(false);

  // Actions
  const toggleUserMenu = () => {
    showUserMenu.value = !showUserMenu.value;
  };

  // Getters
  const currentUser = computed(() => store.state.currentUserDetails);
  const userName = computed(() => store.getters.getUserName);
  const userRole = computed(() => store.getters.getUserRole);
  const userPhoto = computed(() => {
    const photo = currentUser.value?.photo;
    const baseURL = process.env.VUE_APP_BASE_URL || "http://localhost:5000";

    return photo
      ? photo.startsWith("http")
        ? photo
        : `${baseURL}/${photo}`
      : null;
  });
  // School presence checker
  const hasSchool = computed(() => !!store.state.schoolId);

  // Close dropdown on outside click
  onMounted(() => {
    store.dispatch("initializeUserRole");

    document.addEventListener("click", (e) => {
      const el = document.querySelector(".your-profile-wrapper");
      if (el && !el.contains(e.target)) {
        showUserMenu.value = false;
        showSearch.value = false;
      }
    });
  });
  // Logout handler
  const handleLogout = () => {
    localStorage.removeItem("token"); // ✅ clear token
    router.push("/login"); // ✅ redirect to login
  };
</script>

<style scoped>
  @keyframes fade-in {
    from {
      opacity: 0;
      transform: translateY(-10px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  .animate-fade-in {
    animation: fade-in 0.2s ease-out;
  }

  * {
    transition-property: all;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  }

  button:focus {
    outline: 2px solid #4f46e5;
    outline-offset: 2px;
    border-radius: 0.75rem;
  }

  .group:hover .group-hover\:shadow-md {
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
      0 2px 4px -1px rgba(0, 0, 0, 0.06);
  }

  @supports (backdrop-filter: blur(8px)) {
    .backdrop-blur-sm {
      backdrop-filter: blur(8px);
    }
  }
</style>
